<template>
  <router-link :to='pathName(event?.url)' >
    <slot />
    <header v-if='event?.content.date'>
      <h6 v-html='formattedType'/>&ensp;<h5 v-html='humanDaysToGo(event?.content.date)'/>
    </header>
    <h2 v-if='event?.content.date' v-html='prettyDate(event?.content.date)'/>
    <h3 v-if='event?.content.date && location'>
      <span v-html='location?.city' /><template v-if='location?.country'>,
      <em v-html='location?.country' /></template>
    </h3>
    <h4>
      <strong v-html='event?.content.title' /><br />
      <span v-if='event?.content.conductor'>
        Conducted by {{event?.content.conductor}}
      </span>
    </h4>
    <div class="event-programme" v-html='event?.content.programme' />
  </router-link>
</template>

<script setup>
  import { resolveLocation, pathName } from '@/utils';
  import dayjs from 'dayjs';
  import utc from 'dayjs/plugin/utc';
  import timezone from 'dayjs/plugin/timezone';

  // Configure Day.js with timezone support
  dayjs.extend(utc);
  dayjs.extend(timezone);

  // Define props directly as a Vue macro - no import needed
  const props = defineProps(['event']);
  const location = computed(()=>resolveLocation(props.event?.content))
  const prettyDate = (date)=>dayjs(date).format('MMMM D');
  // Compare dates at the calendar day level in user's local timezone
  const daysFromToday = (date) => dayjs(date).startOf('day').diff(dayjs().startOf('day'), 'day');
  const humanDaysToGo = (date) => daysFromToday(date) > 0 ? `IN ${daysFromToday(date)} DAYS` : daysFromToday(date) === 0 ? 'TODAY' : `${Math.abs(daysFromToday(date))} DAYS AGO`;
  const formattedType = computed( () => props.event?.content?.type?.includes('Conductor') ? 'Conducting' : 'Composition');
</script>

<style scoped>
  @import '@/css/breakpoints.css';

  a {
    --darker: var(--gray);
    margin-bottom: 9rem;
  }

  @media (--phone) {
    a {
      width: 100%;
    }
  }

  @media (hover: hover) {
    a:hover {
      --darker: var(--light);
    }
  }

  header {
    display: flex;
  }

  h2 {
    line-height: 1.2em;
    margin-bottom: 0.5rem;
  }

  h3 {
    text-transform: uppercase;
    max-width: 9em;
  }

  h3 em {
    white-space: nowrap;
    overflow: hidden;
  }

  h4 {
    line-height: 1.25;
    color: var(--lightgray);
    max-width: 50ch;
  }

  h4 strong {
    text-transform: uppercase;
  }

  h6 {
    text-transform: uppercase;
  }

  h5 {
    color: var(--gray);
  }

  .event-programme {
    color: var(--darker);
    transition: color 0.5s cubic-bezier(0.42, 0, 0.58, 1);
  }

  .event-programme :deep(ul) {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  .event-programme :deep(li) {
    padding: 0;
    margin: 0;
    list-style: none;
    font-family: var(--serif);
    font-size: 3rem;
    font-style: italic;
    line-height: 0.9em;
    max-width: 8em;
    font-weight: 200;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .event-programme :deep(li strong) {
    font-size: 1.2rem;
    letter-spacing: 0.05em;
    font-family: var(--sans);
    text-transform: uppercase;
    display: block;
    padding: 0;
    margin: 0;
    margin-top: 1em;
    margin-bottom: -0.5em;
    list-style: none;
  }

  .event-programme :deep(li u), .event-programme :deep(li a) {
    text-decoration: none;
  }
</style>
